import { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { Select } from 'antd';
import Swal from 'sweetalert2';
import { toast } from 'sonner';
import { useAcademicYear } from '../../../core/common/context/AcademicYearContext';
import { getAllSubject } from '../../../api/pedagogicElement';
import { getAllTeachers } from '../../../api/teachers';
import { updateCourseForEDTVersion } from '../../../api/edt';
import {
  Action,
  buildPermissionContext,
  Resource,
} from '../../../core/common/autorisations/permissionsMap';

type SelectOption = { id: string; label: string };

type Props = {
  show: boolean;
  course?: any;
  versionId?: string;
  classes: { id: string; name: string; selectLabel?: string }[];
  classrooms: any[];
  onClose: () => void;
  onSaved: () => Promise<void> | void;
};

const EDTCourseSnapshotModal = ({
  show,
  course,
  versionId,
  classes,
  classrooms,
  onClose,
  onSaved,
}: Props) => {
  const { selectedYear } = useAcademicYear();
  const [subjects, setSubjects] = useState<SelectOption[]>([]);
  const [teachers, setTeachers] = useState<SelectOption[]>([]);
  const [subjectId, setSubjectId] = useState('');
  const [teacherId, setTeacherId] = useState('');
  const [numberSession, setNumberSession] = useState(1);
  const [classroomId, setClassroomId] = useState('');
  const [classIds, setClassIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show || !selectedYear?.id) return;
    let cancelled = false;
    const coursePermission = buildPermissionContext(Resource.COURSE, Action.UPDATE);
    Promise.all([
      getAllSubject(selectedYear.id, coursePermission),
      getAllTeachers(coursePermission),
    ])
      .then(([subjectResponse, teacherResponse]) => {
        if (cancelled) return;
        setSubjects((subjectResponse.data || []).map((item: any) => ({ id: item.id, label: item.label })));
        setTeachers((teacherResponse.data || []).map((item: any) => ({
          id: item.id,
          label: [item.firstname, item.lastname].filter(Boolean).join(' '),
        })));
      })
      .catch((error: any) => {
        if (!cancelled) {
          toast.error(error.response?.data?.message || 'Impossible de charger les matières et professeurs.');
        }
      });
    return () => { cancelled = true; };
  }, [show, selectedYear?.id]);

  useEffect(() => {
    if (!show || !course) return;
    setSubjectId(course.Subject?.id || course.id_subject || '');
    setTeacherId(course.TeacherProfil?.id || course.id_teacherProfil || '');
    setNumberSession(Number(course.number_session ?? course.seance_num ?? 1));
    setClassroomId(course.id_classroom || '');
    setClassIds(course.id_classes || []);
  }, [show, course]);

  const save = async (confirmPastTeacherChange = false) => {
    if (!versionId || !course?.id) {
      toast.error("La version d'EDT n'est pas encore chargée.");
      return;
    }
    setLoading(true);
    try {
      await updateCourseForEDTVersion(versionId, course.id, {
        id_subject: subjectId,
        id_teacherProfil: teacherId,
        number_session: numberSession,
        id_classroom: classroomId || null,
        id_classes: classIds,
        confirmPastTeacherChange,
      }, buildPermissionContext(Resource.EDT, Action.UPDATE));
      toast.success('Cours et placements mis à jour dans cette version EDT.');
      await onSaved();
      onClose();
    } catch (error: any) {
      const response = error.response?.data;
      if (response?.requiresConfirmation && !confirmPastTeacherChange) {
        setLoading(false);
        const confirmation = await Swal.fire({
          title: 'Séances déjà passées',
          text: `${response.pastSessionCount} séance(s) passée(s) seront affichées sous le nouveau professeur et compteront pour lui dans les rapports mensuels. Les absences, suivis et notes garderont leurs liens actuels. Confirmer ?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Confirmer le changement',
          cancelButtonText: 'Garder le professeur actuel',
        });
        if (confirmation.isConfirmed) await save(true);
        return;
      }
      toast.error(response?.message || response?.error || 'Impossible de modifier ce cours.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Modifier le cours dans cet EDT</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info">
          Ces réglages sont enregistrés dans cette version uniquement. Les classes ajoutées ou retirées sont répercutées sur les placements de cet EDT. Les séances passées ou avec un historique ne peuvent pas être modifiées. Les UE et les notes du cours source ne sont pas modifiées.
        </Alert>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Matière</Form.Label>
            <Form.Select value={subjectId} onChange={(event) => setSubjectId(event.target.value)}>
              <option value="">Choisir une matière</option>
              {subjects.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Professeur</Form.Label>
            <Form.Select value={teacherId} onChange={(event) => setTeacherId(event.target.value)}>
              <option value="">Choisir un professeur</option>
              {teachers.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
            </Form.Select>
          </Form.Group>
          <div className="row">
            <Form.Group className="col-md-6 mb-3">
              <Form.Label>Nombre de séances</Form.Label>
              <Form.Control
                type="number"
                min={1}
                step={1}
                value={numberSession}
                onChange={(event) => setNumberSession(Number(event.target.value))}
              />
              <Form.Text>La durée de tous les placements de ce cours dans cet EDT sera ajustée.</Form.Text>
            </Form.Group>
            <Form.Group className="col-md-6 mb-3">
              <Form.Label>Salle par défaut des prochains placements</Form.Label>
              <Form.Select value={classroomId} onChange={(event) => setClassroomId(event.target.value)}>
                <option value="">Aucune salle par défaut</option>
                {classrooms.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
              </Form.Select>
            </Form.Group>
          </div>
          <Form.Group className="mb-2">
            <Form.Label>Classes associées à la copie locale</Form.Label>
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              value={classIds}
              onChange={(values: string[]) => setClassIds(values)}
              options={classes.map((item) => ({
                value: item.id,
                label: item.selectLabel || item.name,
              }))}
              getPopupContainer={(trigger) => trigger.parentElement || document.body}
              maxTagCount="responsive"
              placeholder="Sélectionner une ou plusieurs classes"
              style={{ width: '100%' }}
            />
            <Form.Text>
              Les classes ajoutées reprendront les créneaux déjà placés. Les conflits avec une autre séance de la classe empêcheront l’enregistrement.
            </Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-end gap-2">
        <Button variant="danger" onClick={onClose} disabled={loading}>Fermer</Button>
        <Button
          variant="primary"
          onClick={() => save()}
          disabled={loading || !subjectId || !teacherId || numberSession < 1 || classIds.length === 0}
        >
          {loading ? <><Spinner size="sm" className="me-2" />Enregistrement…</> : 'Enregistrer dans cet EDT'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EDTCourseSnapshotModal;
